import type { GridCellParams } from '@mui/x-data-grid';
import {
  Box,
  Typography,
  Tooltip,
  styled,
  tooltipClasses,
  type TooltipProps,
} from '@mui/material';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import * as accountingSlice from '../../../data/accounting.slice';
import {
  fetchChargeTypesAsync,
  selectChargeTypes,
} from '../../../data/reference.slice';
import {
  fetchTripLegAddOnsAsync,
  selectTripLegAddOns,
} from '../../../data/trip.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useEffect } from 'react';
import { PricingContent } from './PricingContent';

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    borderRadius: 3,
    background: '#FFF',
    boxShadow:
      '0px 5px 5px -3px rgba(0, 0, 0, 0.10), 0px 8px 10px 1px rgba(0, 0, 0, 0.07), 0px 3px 14px 2px rgba(0, 0, 0, 0.06)',
    color: 'rgba(1, 16, 18, 0.90)',
    padding: theme.spacing(1, 2),
    width: 308,
  },
}));

export default function PriceTooltipAsync({
  params,
  classes,
}: {
  params: GridCellParams;
  classes: Record<string, string>;
}): React.ReactElement {
  const dispatch = useAppDispatch();

  const pricings = useAppSelector(accountingSlice.selectTripLegPricings);
  const chargeTypes = useAppSelector(selectChargeTypes);
  const addOnLineItems = useAppSelector(selectTripLegAddOns);

  useEffect(() => {
    if (!chargeTypes?.length) {
      dispatch(fetchChargeTypesAsync());
    }
  }, [chargeTypes]);

  const {
    price,
    passengerUUID,
    tripLegReferenceID,
    tripLegID,
    hasRequiredTimestamps,
  } = params.row;

  const handleOpenTooltip = (event: React.SyntheticEvent) => {
    dispatch(
      accountingSlice.fetchTripLegPricingsAsync(
        passengerUUID,
        tripLegReferenceID,
      ),
    );
    if (tripLegID) {
      dispatch(fetchTripLegAddOnsAsync(tripLegID));
    }
  };

  if (!hasRequiredTimestamps) {
    return <></>;
  }

  const priceValue = price?.replace(/[$,]/g, '').trim();
  const numericPrice = priceValue ? parseFloat(priceValue) : 0;
  if (!priceValue || numericPrice <= 0) {
    return <></>;
  }

  return (
    <Box className={classes.cellContent}>
      <LightTooltip
        title={
          <PricingContent
            tripLeg={params.row.tripLeg || params.row}
            pricings={pricings}
            chargeTypes={chargeTypes}
            addOnLineItems={addOnLineItems}
          />
        }
        arrow
        placement="bottom-end"
        onOpen={handleOpenTooltip}>
        <Typography className={classes.primaryText}>{price}</Typography>
      </LightTooltip>
    </Box>
  );
}
